import React, { useState, useMemo, useCallback, useEffect, Suspense, lazy } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import routes from '../../config/settings/routes'
import {
  usePathParam,
  useQuery,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  Button,
  CheckBox,
  Dialog,
  Toast,
  Typography,
  useTextarea,
  useViewport,
  Skeleton
} from '@chilecompra/react-kit'
import { useReCaptcha } from '../../modules/hooks/reCaptcha'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import Errors from '../../components/Custom/Errors'
import { onCloseSendSellerQuoteDialog, onAcceptAndPublishSellerQuoteBidThunk } from './SendSellerQuoteDialog.actions'
import { onClearAttachedFilesSuccess, onSaveQuoteSellerSuccess } from '../QuoteBidSeller/QuoteBidSeller.actions'
import SendSellerQuoteDialogListText from './SendSellerQuoteDialog.ListText'
import { DialogWrapper, Grid, TextArea, Radio, Tooltip, LinkButton } from './SendSellerQuoteDialog.styles'
import { radioOptions, leyUrl, columns } from './SendSellerQuoteDialog.constants'
import { calculateIdRespuesta, mapIdRespuestaToLeyCheck, createHandleChange } from './SendSellerQuoteDialog.utils'
import { getCookieFromDomain } from '../../modules/cookies'

const DJAditionalDetailInfo = lazy(() => import('../../components/Custom/DJAditionalDetail'))
const DynamicTable = lazy(() => import('../../components/Custom/DynamicInputsTable'))

/**
 * The SendSellerQuoteDialog's container.
 */
const SendSellerQuoteDialog = () => {
  const { isPrivate } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha(isPrivate, 'newQuote')
  const { errors, error, loading, open, showAbilityAffidavit } = useSelector(state => state.sendSellerQuoteDialog)
  const {
    quoteBidDetail: { businessDni, businessName, name, sendDate, DJAditionalDetail, DJBeneficiaries, DJIdRespuesta },
    loading: quoteBidLoading
  } = useSelector(state => state.quoteBid)
  const { quoteBidSeller: quoteBidSellerState } = useSelector(state => state.quoteBidSeller)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const quoteCode = usePathParam('quoteId')
  const labelButton = 'Aceptar y enviar cotización'

  const userInfo = getCookieFromDomain('user_info')
  const userInfoData = Object.fromEntries(new URLSearchParams(userInfo))

  const {
    size: { isDownToTiny }
  } = useViewport()

  const { value: textConflict, onChange: handleTextChange } = useTextarea({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(300)],
    overwriteCallback: () => textConflict
  })

  const businessLabel = useMemo(
    () => (businessName ? `${businessName} RUT ${businessDni || ''}` : ''),
    [businessDni, businessName]
  )

  const [loadingCaptcha, setLoadingCaptcha] = useState(false)
  const [leyCheck, setLeyCheck] = useState({
    1: '2',
    2: '',
    3: false
  })
  const [rows, setRows] = useState([{ razonSocial: '', rut: '' }])
  const [inputHasErrors, setInputHasErrors] = useState(false)

  const idRespuesta = useMemo(() => calculateIdRespuesta(leyCheck), [leyCheck])

  const handleChange = useCallback(createHandleChange(setLeyCheck), [setLeyCheck])

  const handleOnClose = () => dispatch(onCloseSendSellerQuoteDialog())

  const handleErrorChange = errors => {
    setInputHasErrors(errors)
  }

  const handleSend = async () => {
    let tokenReCaptcha = ''
    try {
      setLoadingCaptcha(true)
      tokenReCaptcha = await executeReCaptcha()
    } catch (error) {
      tokenReCaptcha = ''
    } finally {
      setLoadingCaptcha(false)
    }

    batch(() => {
      const updatedQuoteBidSeller = { ...quoteBidSellerState, idRespuesta, tokenReCaptcha }

      if (leyCheck[2] === '1') {
        const beneficiarios = rows
          .filter(row => row.razonSocial?.trim() || row.rut?.trim())
          .map((row, index) => ({
            id: index + 1,
            ...row
          }))

        updatedQuoteBidSeller.beneficiarios = beneficiarios
      }

      if (textConflict?.trim()) {
        updatedQuoteBidSeller.detalleAdicional = textConflict
      }

      dispatch(onSaveQuoteSellerSuccess({ quoteBidSeller: updatedQuoteBidSeller }))

      dispatch(
        onAcceptAndPublishSellerQuoteBidThunk({
          callback: () => {
            query.set('quote', quoteCode)
            navigate({ pathname: routes.sellerQuotePublishedSuccessfully, search: query.toString() }, { replace: true })
          },
          userHost: userInfoData.host || ''
        })
      )
      dispatch(onCloseSendSellerQuoteDialog())
      dispatch(onClearAttachedFilesSuccess())
    })
  }

  const disabledSubmit = useMemo(() => {
    const { 1: radio1, 2: radio2, 3: checkbox } = leyCheck

    switch (true) {
      case !checkbox || radio1 === '' || inputHasErrors:
        return true

      case checkbox && radio1 === '1' && radio2 === '':
        return true

      case checkbox &&
        radio1 === '1' &&
        radio2 === '1' &&
        rows.some(({ razonSocial, rut }) => !razonSocial?.trim() || !rut?.trim()):
        return true

      default:
        return false
    }
  }, [rows, leyCheck, inputHasErrors])

  useEffect(() => {
    if (!showAbilityAffidavit) return

    if (DJBeneficiaries?.length) setRows(DJBeneficiaries)

    if (DJIdRespuesta) setLeyCheck(mapIdRespuestaToLeyCheck(DJIdRespuesta))
  }, [DJBeneficiaries, showAbilityAffidavit, DJIdRespuesta])

  useEffect(() => {
    if (DJIdRespuesta) return

    if (leyCheck[1] === '1') {
      setLeyCheck(prev => ({ ...prev, 2: '2' }))
    } else if (leyCheck[1] === '2') {
      setLeyCheck(prev => ({ ...prev, 2: '' }))
    }
  }, [leyCheck[1]])

  const renderButtonSection = () => {
    return !showAbilityAffidavit ? (
      <Grid container justifyContent="center" margin="48px 0 0">
        <Button
          color="success"
          disabled={disabledSubmit}
          loading={loading || loadingCaptcha}
          onClick={handleSend}
          variant="contained"
          width={isDownToTiny ? '100%' : undefined}
        >
          {isDownToTiny ? labelButton.substring(0, 10) + '...' : labelButton}
        </Button>
      </Grid>
    ) : (
      <Grid container justifyContent="center">
        <Button onClick={handleOnClose} variant="outlined" width={isDownToTiny ? '100%' : undefined}>
          cerrar
        </Button>
      </Grid>
    )
  }

  const renderInterestConflict = () => {
    if (showAbilityAffidavit) {
      return (
        <Suspense fallback={<Skeleton />}>
          <DJAditionalDetailInfo DJAditionalDetail={DJAditionalDetail} loading={quoteBidLoading} />
        </Suspense>
      )
    }

    return (
      <>
        <Typography variant="body2" align="justify">
          Si identificas un posible conflicto de interés según lo declarado, debes proporcionar la información necesaria
          para que el organismo comprador revise y evalúe la situación.
        </Typography>
        <TextArea
          margin="20px 0 20px 0"
          label="Detalle el posible conflicto de interés:"
          maxCount={300}
          maxLength={300}
          tooltip="Cuando circunstancias excepcionales lo hagan necesario, los organismos del Estado podrán celebrar dichos contratos, siempre que se ajusten a condiciones de equidad similares a las que habitualmente prevalecen en el mercado. La aprobación del contrato deberá hacerse por resolución fundada, que se comunicará al superior jerárquico del suscriptor, a la Contraloría General de la República y a la Cámara de Diputados, en el caso de los órganos de la Administración del Estado y en el caso del Poder Judicial, a su Comisión de Ética."
          onChange={handleTextChange}
          value={textConflict}
        />
      </>
    )
  }

  const renderRelatedBussines = () => {
    if (showAbilityAffidavit && !DJIdRespuesta) return null

    return (
      <Suspense fallback={<Skeleton />}>
        <Grid border="1px solid #B3BECB" borderRadius="5px" padding="0.8rem" margin="0 0 30px 0">
          <Typography display="flex" variant="body1" align="justify" fontWeight="bold">
            Empresas relacionadas
            <Tooltip
              content={
                <span>
                  Las empresas relacionadas son aquellas que pertenecen al mismo grupo empresarial o tienen algún tipo
                  de relación entre sí. Para más información, revisa el detalle directamente en la ley
                  <LinkButton
                    color="primary"
                    padding="0"
                    size="small"
                    variant="text"
                    onClick={() => window.open(leyUrl)}
                  >
                    haciendo click aquí.
                  </LinkButton>
                </span>
              }
            />
          </Typography>

          <Typography variant="body2" align="justify" margin="1rem 0 0 0">
            ¿Tu representada forma parte de un <strong>grupo empresarial</strong> o{' '}
            <strong>tiene relación con otras personas</strong>, según lo establecido en el artículo 9° de la Ley N°
            19.886?
          </Typography>
          <Radio
            value={leyCheck[1]}
            name={1}
            direction="horizontal"
            disabled={showAbilityAffidavit}
            options={radioOptions}
            onChange={handleChange}
          />

          {leyCheck[1] === '1' && (
            <>
              <Typography variant="body2" align="justify" margin="0.1rem 0 0 0">
                ¿Alguno de los miembros del grupo empresarial o de sus personas relacionadas{' '}
                <strong>participa en el presente procedimiento</strong> de compra ofertando respecto del mismo producto
                o servicio?
              </Typography>
              <Radio
                value={leyCheck[2]}
                name={2}
                direction="horizontal"
                disabled={showAbilityAffidavit}
                options={radioOptions}
                onChange={handleChange}
              />
            </>
          )}

          {leyCheck[1] === '1' && leyCheck[2] === '1' && (
            <DynamicTable
              onErrorChange={handleErrorChange}
              rows={rows}
              setRows={setRows}
              columns={columns}
              readOnly={showAbilityAffidavit}
            />
          )}
        </Grid>
      </Suspense>
    )
  }

  const renderErrorSection = () => (
    <Grid margin="24px 0 0">
      <Errors errors={errors} />
      {!loading && error && (
        <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}
    </Grid>
  )

  return (
    <Dialog
      disableBackdropClick
      margin="100px"
      maxWidth="910px"
      open={open}
      verticalAlign="top"
      onClose={handleOnClose}
    >
      <DialogWrapper>
        <Grid container>
          <Typography fontWeight="bold" variant="h2">
            {showAbilityAffidavit ? 'Declaración Jurada de Requisitos para Ofertar' : 'Un último paso'}
          </Typography>
        </Grid>
        <Grid container margin="16px 0 0">
          {showAbilityAffidavit ? (
            <Typography display="inline" variant="body2">
              Con fecha
              <Typography color="error" display="inline" margin="0 0 0 4px" variant="body2">
                {quoteBidLoading ? <Skeleton width={60} /> : sendDate}
              </Typography>
              ,
              <Typography color="error" display="inline" margin="0 0 0 4px" variant="body2">
                {quoteBidLoading ? <Skeleton width={100} /> : name}
              </Typography>
              , ha declarado bajo juramento que no se encuentra afecto(a), o, si es el caso, su representada
              <Typography color="error" display="inline" margin="0 4px" variant="body2">
                {quoteBidLoading ? <Skeleton width={100} /> : businessLabel}
              </Typography>
              no se encuentra afecta, a ninguna de las inhabilidades legales que se mencionan en esta declaración:
            </Typography>
          ) : (
            <Typography variant="body2">
              Antes de enviar tu cotización, debes confirmar el cumplimiento de los siguientes requisitos:
            </Typography>
          )}
        </Grid>
        <Grid container margin="32px 0">
          <SendSellerQuoteDialogListText showAbilityAffidavit={showAbilityAffidavit} />
        </Grid>
        <Grid container>
          {renderInterestConflict()}
          {renderRelatedBussines()}
          {!showAbilityAffidavit && (
            <CheckBox
              checked={leyCheck[3]}
              name={3}
              label="Declaro que toda la información ingresada en este formulario es veraz, completa, verificable y se encuentra actualizada. Debe tenerse presente que faltar a la verdad respecto de lo informado en una declaración jurada puede traducirse en la comisión del delito de perjurio, en virtud del artículo 210 del Código Penal, que dispone que 'el que ante la autoridad o sus agentes perjurare o diere falso testimonio en materia que no sea contenciosa, sufrirá las penas de presidio menor en sus grados mínimo a medio y multa de seis a diez unidades tributarias mensuales.'"
              onChange={handleChange}
              align="justify"
            />
          )}
        </Grid>
        {renderErrorSection()}
        {renderButtonSection()}
      </DialogWrapper>
    </Dialog>
  )
}

export default SendSellerQuoteDialog
